import { useEffect, useState, useCallback } from 'react';
import { Typography, Box, Divider } from '@mui/material';

import { useApi } from '@/providers/ApiContext';
import { getPlotSections } from '@/actions/plotSections';
import PlotSection from './PlotSection';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import type {
  PlotSection as PlotSectionType,
  EntityId,
} from '@/types';

interface PlotContentsProps {
  plotId: string | undefined;
  refreshTrigger: number;
  plotStatus?: string;
}

function PlotContents({
  plotId,
  refreshTrigger,
  plotStatus,
}: PlotContentsProps): React.JSX.Element {
  const api = useApi();
  const [sections, setSections] = useState<PlotSectionType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);

  const fetchSections = useCallback(async () => {
    if (!plotId || !api) return;

    try {
      setLoading(true);
      setApiError(null);
      const data = await getPlotSections(api, plotId as EntityId);
      setSections(data.results || []);
    } catch (err) {
      console.error('Error fetching plot sections:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Unable to load plot sections. This could be due to network issues, server problems, or the plot may not exist.';
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [api, plotId]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  // Refresh sections when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger) {
      fetchSections();
    }
  }, [refreshTrigger, fetchSections]);

  return (
    <Box>
      <Typography
        variant='h6'
        sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}
      >
        Plot Body
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {loading ? (
        <Typography variant='body2' color='text.secondary'>
          Loading plot sections...
        </Typography>
      ) : sections.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          No plot sections found for this plot.
        </Typography>
      ) : (
        sections.map((section, index) => (
          <PlotSection
            key={section.entity_id || index}
            section={section}
            plotId={plotId}
            refreshTrigger={refreshTrigger}
            plotStatus={plotStatus}
          />
        ))
      )}

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Box>
  );
}

export default PlotContents;

